import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { KpiCard, KpiGrid } from 'molecules';
import { formatMoneyCurrency } from 'core/utils/format';
import type { ICurrencyMoneyModel } from 'core/utils/groupMoney';

import { GroupColors } from './groupColors';

interface IMoneyModelGridProps {
  /**
   * One row per currency present in the group's **active** agreements.
   *
   * This component is intentionally pure/presentational: the caller (the
   * `FinancesTab`, task 14.2) computes the rows via
   * `buildMoneyModel(contributions, invoiceAggByCurrency)` from
   * `core/utils/groupMoney` and passes them in. The caller also owns the
   * loading / error / empty terminal states (it wraps this grid in
   * `TabStateView`), so when `rows` is empty this component renders nothing.
   */
  rows: ICurrencyMoneyModel[];
}

/** Marker for an invoice-side figure that is absent for a currency (Req 6.5). */
const ABSENT = '—';

/**
 * MoneyModelGrid — the per-currency Money_Model surface of the FinancesTab
 * (Req 5–6).
 *
 * For every currency row it renders the four Money_Model stages in order —
 * **Planned → Issued → Paid → Debt** (Req 5.1, 6.1) — as a KPI card row, with
 * each amount formatted through the shared `formatMoneyCurrency(amount,
 * row.currency)` so it always carries that row's own currency code and never a
 * hardcoded one (Req 6.2, 6.4). Currencies are never summed together; each
 * currency gets its own section (Req 6.3).
 *
 * Source labeling (Req 5.2): Planned and Debt are labeled contract-side
 * (contributions), Issued is labeled invoice-side (Fakturownia), and Paid is
 * the received amount. A localized explanation block (Req 5.3) spells out the
 * difference between the contract source (contributions) and the invoice source
 * (Fakturownia) once at the top.
 *
 * When a currency has no invoice data (`hasInvoiceData === false` /
 * `issued === null`) its contract-side figures still render; only the Issued
 * cell is marked absent («—») and the currency heading flags that invoice-side
 * data is absent (Req 6.5).
 *
 * Terminology parity (Req 5.4): the four stage names come from the shared
 * `pages.groupDetail.money.*` i18n keys, the same keys KpiStrip and FinancesTab
 * reference, so there is exactly one localized term per stage. All copy
 * resolves through i18next under `pages.groupDetail.*`, and every color comes
 * from the shared `GroupColors` theme tokens (no hardcoded hex / inline color
 * styles).
 */
export const MoneyModelGrid = memo(({ rows }: IMoneyModelGridProps) => {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <Title>{t('pages.groupDetail.money.title')}</Title>
      <Explanation>
        {t('pages.groupDetail.money.sourceExplanation')}
      </Explanation>

      {rows.map((row) => {
        const hasInvoice = row.hasInvoiceData && row.issued !== null;
        return (
          <CurrencySection key={row.currency || 'unknown'}>
            <CurrencyHeading>
              <CurrencyCode>{row.currency || ABSENT}</CurrencyCode>
              {!hasInvoice && (
                <InvoiceAbsentBadge>
                  {t('pages.groupDetail.money.invoiceAbsent')}
                </InvoiceAbsentBadge>
              )}
            </CurrencyHeading>

            <KpiGrid columns={4}>
              <KpiCard
                label={t('pages.groupDetail.money.planned')}
                value={formatMoneyCurrency(row.planned, row.currency)}
                subLabel={t('pages.groupDetail.money.source.contract')}
              />
              <KpiCard
                label={t('pages.groupDetail.money.issued')}
                value={
                  hasInvoice
                    ? formatMoneyCurrency(row.issued, row.currency)
                    : ABSENT
                }
                subLabel={
                  hasInvoice
                    ? t('pages.groupDetail.money.source.invoice')
                    : t('pages.groupDetail.money.invoiceAbsent')
                }
                accent={hasInvoice ? undefined : GroupColors.textMuted}
              />
              <KpiCard
                label={t('pages.groupDetail.money.paid')}
                value={formatMoneyCurrency(row.paid, row.currency)}
                subLabel={t('pages.groupDetail.money.source.received')}
                accent={GroupColors.success}
              />
              <KpiCard
                label={t('pages.groupDetail.money.debt')}
                value={formatMoneyCurrency(row.debt, row.currency)}
                subLabel={t('pages.groupDetail.money.source.contract')}
                accent={row.debt > 0 ? GroupColors.warn : undefined}
              />
            </KpiGrid>
          </CurrencySection>
        );
      })}
    </Wrapper>
  );
});

const Wrapper = styled.div`
  margin-top: 16px;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${GroupColors.textPrimary};
  margin-bottom: 8px;
`;

const Explanation = styled.p`
  margin: 0 0 16px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
  color: ${GroupColors.textSecondary};
  background: ${GroupColors.surfaceMuted};
  border: 1px solid ${GroupColors.border};
  border-radius: 8px;
`;

const CurrencySection = styled.section`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CurrencyHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const CurrencyCode = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${GroupColors.textPrimary};
`;

const InvoiceAbsentBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  color: ${GroupColors.warn};
  background: ${GroupColors.surfaceSubtle};
  border: 1px solid ${GroupColors.border};
`;
